import React, { useState, useEffect } from 'react';
import { api } from './api.js';
import { useLang } from './i18n.js';
import { Overlay, Chip } from './ui.jsx';

/**
 * The call log.
 *
 * This is the screen the whole redesign is betting on. It has to capture a real
 * conversation in the ninety seconds after it ends, from a phone, in a taxi —
 * so: chips instead of dropdowns wherever the answer is a small known set, only
 * the outcome is required, and the next step reveals exactly one extra block
 * rather than a wall of optional fields.
 */
export function CallForm({ target, config, me, onClose, onSaved, toast }) {
  const { t } = useLang();
  const [f, setF] = useState({
    outcome: config.call_outcomes?.[0] || 'Connected',
    interest: '',
    duration_bucket: '',
    contact_name: target?.contact_name || '',
    contact_title: '',
    phone: target?.phone || '',
    email: '',
    current_vendor: '',
    budget_signal: '',
    services: [],
    buying_window: '',
    pain_point: '',
    objection: '',
    next_step: 'none',
    follow_up_at: '',
    follow_up_note: '',
    onboard_entity: target?.entity_tag || me.entities[0] || 'BTM',
    onboard_owner_id: me.id,
    meeting_at: '',
    meeting_format: config.meeting_formats?.[0] || '',
    meeting_their_attendees: '',
    meeting_our_attendees: '',
  });
  const [owners, setOwners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleService = (s) =>
    setF((st) => ({
      ...st,
      services: st.services.includes(s)
        ? st.services.filter((x) => x !== s)
        : [...st.services, s],
    }));

  useEffect(() => {
    if (f.next_step === 'onboard_lead' && owners.length === 0) {
      api.users({ role: 'sales' }).then((d) => setOwners(d.users)).catch(() => {});
    }
  }, [f.next_step, owners.length]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const body = {
        prospect_id: target?.prospect_id ?? null,
        company_id: target?.company_id ?? null,
        outcome: f.outcome,
        interest: f.interest || undefined,
        duration_bucket: f.duration_bucket || undefined,
        contact_name: f.contact_name || undefined,
        contact_title: f.contact_title || undefined,
        phone: f.phone || undefined,
        email: f.email || undefined,
        current_vendor: f.current_vendor || undefined,
        budget_signal: f.budget_signal || undefined,
        services: f.services.length ? f.services : undefined,
        buying_window: f.buying_window || undefined,
        pain_point: f.pain_point || undefined,
        objection: f.objection || undefined,
        next_step: f.next_step,
      };
      if (f.next_step === 'follow_up_call') {
        body.follow_up_at = f.follow_up_at || undefined;
        body.follow_up_note = f.follow_up_note || undefined;
      }
      if (f.next_step === 'onboard_lead') {
        body.onboard_entity = f.onboard_entity;
        body.onboard_owner_id = f.onboard_owner_id;
      }
      if (f.next_step === 'schedule_meeting') {
        body.meeting_at = f.meeting_at || undefined;
        body.meeting_format = f.meeting_format || undefined;
        body.meeting_their_attendees = f.meeting_their_attendees || undefined;
        body.meeting_our_attendees = f.meeting_our_attendees || undefined;
      }
      const res = await api.logCall(body);
      toast(res.company_code ? `${t('logged')} → ${res.company_code}` : t('logged'));
      onSaved(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const Section = ({ label, children }) => (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
  const Row = ({ children }) => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>
  );

  return (
    <Overlay onClose={onClose} kind="drawer">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="drawer-head" style={{ paddingBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 19 }}>
                {t('logCallTitle')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                {target?.label}
                {target?.attempts ? ` · ${t('attempts')} ${target.attempts}` : ''}
              </div>
            </div>
            <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="drawer-body" style={{ paddingTop: 4 }}>
          <Section label={t('outcome')}>
            <Row>
              {(config.call_outcomes || []).map((o) => (
                <Chip key={o} on={f.outcome === o} onClick={() => set('outcome', o)}>
                  {o}
                </Chip>
              ))}
            </Row>
          </Section>

          <Section label={t('interestLevel')}>
            <Row>
              {['hot', 'warm', 'cold', 'none'].map((o) => (
                <Chip key={o} on={f.interest === o} onClick={() => set('interest', f.interest === o ? '' : o)}>
                  {o}
                </Chip>
              ))}
            </Row>
          </Section>

          <Section label={t('callDuration')}>
            <Row>
              {(config.call_durations || []).map((o) => (
                <Chip
                  key={o}
                  on={f.duration_bucket === o}
                  onClick={() => set('duration_bucket', f.duration_bucket === o ? '' : o)}
                >
                  {o}
                </Chip>
              ))}
            </Row>
          </Section>

          <Section label={t('whoYouSpokeTo')}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Field label={t('name')} v={f.contact_name} on={(v) => set('contact_name', v)} />
              <Field label={t('roleTitle')} v={f.contact_title} on={(v) => set('contact_title', v)} />
              <Field label={t('directLine')} v={f.phone} on={(v) => set('phone', v)} />
              <Field label={t('email')} v={f.email} on={(v) => set('email', v)} type="email" />
            </div>
          </Section>

          <Section label={t('currentVendor')}>
            <input
              className="input"
              value={f.current_vendor}
              onChange={(e) => set('current_vendor', e.target.value)}
            />
          </Section>

          <Section label={t('budgetSignal')}>
            <Row>
              {(config.budget_signals || []).map((o) => (
                <Chip
                  key={o}
                  on={f.budget_signal === o}
                  onClick={() => set('budget_signal', f.budget_signal === o ? '' : o)}
                >
                  {o}
                </Chip>
              ))}
            </Row>
          </Section>

          <Section label={t('servicesNeeded')}>
            <Row>
              {(config.services || []).map((s) => (
                <Chip key={s} on={f.services.includes(s)} onClick={() => toggleService(s)}>
                  {s}
                </Chip>
              ))}
            </Row>
          </Section>

          <Section label={t('buyingWindow')}>
            <Row>
              {[
                ['now', t('now')],
                ['this_quarter', t('thisQuarter')],
                ['next_quarter', t('nextQuarter')],
                ['next_fy', t('nextFy')],
                ['unknown', t('unknown')],
              ].map(([v, label]) => (
                <Chip key={v} on={f.buying_window === v} onClick={() => set('buying_window', v)}>
                  {label}
                </Chip>
              ))}
            </Row>
          </Section>

          <Section label={t('painPoint')}>
            <textarea
              className="textarea"
              rows={3}
              value={f.pain_point}
              onChange={(e) => set('pain_point', e.target.value)}
            />
          </Section>

          <Section label={t('objectionRaised')}>
            <Row>
              {(config.objections || []).map((o) => (
                <Chip
                  key={o}
                  on={f.objection === o}
                  onClick={() => set('objection', f.objection === o ? '' : o)}
                >
                  {o}
                </Chip>
              ))}
            </Row>
          </Section>

          {/* The next step reveals exactly one block. A form that shows every
              branch at once is a form nobody finishes on a phone. */}
          <Section label={t('whatNext')}>
            <Row>
              {[
                ['follow_up_call', t('stepFollowUp')],
                ['onboard_lead', t('stepOnboard')],
                ['schedule_meeting', t('stepMeeting')],
                ['none', t('stepNone')],
              ].map(([v, label]) => (
                <Chip key={v} on={f.next_step === v} onClick={() => set('next_step', v)}>
                  {label}
                </Chip>
              ))}
            </Row>
          </Section>

          {f.next_step === 'follow_up_call' && (
            <Branch>
              <Field
                label={t('due')}
                type="date"
                v={f.follow_up_at}
                on={(v) => set('follow_up_at', v)}
              />
              <Field
                label={t('purpose')}
                v={f.follow_up_note}
                on={(v) => set('follow_up_note', v)}
              />
            </Branch>
          )}

          {f.next_step === 'onboard_lead' && (
            <Branch>
              <div>
                <label className="field-label">{t('entity')}</label>
                <select
                  className="select"
                  value={f.onboard_entity}
                  onChange={(e) => set('onboard_entity', e.target.value)}
                >
                  {(me.entities.length ? me.entities : ['BTM', 'MR']).map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="field-label">{t('assignOwner')}</label>
                <select
                  className="select"
                  value={f.onboard_owner_id || ''}
                  onChange={(e) => set('onboard_owner_id', e.target.value)}
                >
                  <option value={me.id}>{me.name}</option>
                  {owners
                    .filter((u) => u.id !== me.id)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                </select>
              </div>
            </Branch>
          )}

          {f.next_step === 'schedule_meeting' && (
            <Branch>
              <Field
                label={t('due')}
                type="datetime-local"
                v={f.meeting_at}
                on={(v) => set('meeting_at', v)}
              />
              <div>
                <label className="field-label">{t('format')}</label>
                <select
                  className="select"
                  value={f.meeting_format}
                  onChange={(e) => set('meeting_format', e.target.value)}
                >
                  {(config.meeting_formats || []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <Field
                label={t('theirAttendees')}
                v={f.meeting_their_attendees}
                on={(v) => set('meeting_their_attendees', v)}
              />
              <Field
                label={t('ourAttendees')}
                v={f.meeting_our_attendees}
                on={(v) => set('meeting_our_attendees', v)}
              />
              <div style={{ gridColumn: '1/-1', fontSize: 11.5, color: 'var(--ink-3)' }}>
                {t('invitesNote')}
              </div>
            </Branch>
          )}

          {error && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 14 }}>{error}</div>
          )}
        </div>

        <div className="drawer-foot">
          <button type="button" className="btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy}>
            {t('saveCall')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

const Branch = ({ children }) => (
  <div
    style={{
      marginTop: 12,
      padding: 12,
      borderRadius: 'var(--r-md)',
      background: 'var(--sunken)',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 10,
    }}
  >
    {children}
  </div>
);

function Field({ label, v, on, type = 'text' }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input className="input" type={type} value={v} onChange={(e) => on(e.target.value)} />
    </div>
  );
}
